import { type JSX, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { RateCardStrategy } from '@/hooks/useAdminRateCards';

/**
 * Inline help icon (ⓘ) that opens a tooltip explaining the three rate-card
 * routing strategies: lcr, lcd, priority.
 *
 * Uses `position: fixed` (not absolute) so the tooltip escapes any
 * overflow:hidden ancestor — important because this lives inside modals
 * and table cells that clip absolute children. Coords are recalculated
 * from the trigger's getBoundingClientRect on open + scroll/resize.
 *
 * Click-to-toggle for keyboard accessibility; closes on outside click.
 */
export function RoutingStrategyHelp({ className }: { className?: string }): JSX.Element {
  const { t } = useTranslation('default');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Recompute position when opened, on resize, and on scroll (catches
  // scrolling within the modal body or the page itself).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const recompute = (): void => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const popoverWidth = 340;
      // Prefer right of the icon; flip to left if it would overflow the
      // viewport. Vertically center on the icon, then clamp to viewport.
      let left = rect.right + 8;
      if (left + popoverWidth > window.innerWidth - 8) {
        left = Math.max(8, rect.left - popoverWidth - 8);
      }
      const top = Math.min(
        Math.max(8, rect.top + rect.height / 2 - 110),
        window.innerHeight - 240,
      );
      setCoords({ top, left });
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true); // capture for nested scrollers
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open]);

  // Close when clicking outside both the trigger and the popover.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    // mousedown so we close before a click event lands on the trigger
    // (which would re-toggle to closed→open).
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <span className={cn('relative inline-flex items-center', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-text-4 text-text-4 text-[10px] font-semibold hover:text-text hover:border-text leading-none"
        aria-label={t('rsh_open')}
      >
        ?
      </button>
      {open && coords && (
        <div
          ref={popoverRef}
          role="tooltip"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: 340 }}
          className="z-50 bg-surface border border-line rounded-lg shadow-lg p-4 text-[12px] text-text leading-relaxed"
        >
          <div className="font-semibold text-text-2 mb-2">{t('rsh_title')}</div>
          <p className="text-text-3 mb-3">{t('rsh_intro')}</p>
          <ul className="space-y-2.5">
            <li>
              <Badge variant="success">LCR</Badge>
              <span className="ml-1.5 text-text-3">{t('rsh_lcr')}</span>
            </li>
            <li>
              <Badge variant="neutral">LCD</Badge>
              <span className="ml-1.5 text-text-3">{t('rsh_lcd')}</span>
            </li>
            <li>
              <Badge variant="warn">PRIORITY</Badge>
              <span className="ml-1.5 text-text-3">{t('rsh_priority')}</span>
            </li>
          </ul>
          <p className="text-text-4 text-[11px] mt-3 pt-3 border-t border-line-soft">
            {t('rsh_tip')}
          </p>
        </div>
      )}
    </span>
  );
}

/** Compact pill that shows the strategy in lists / table rows / headers. */
export function StrategyBadge({ strategy }: { strategy: RateCardStrategy }): JSX.Element {
  switch (strategy) {
    case 'lcr':
      return <Badge variant="success">LCR</Badge>;
    case 'lcd':
      return <Badge variant="neutral">LCD</Badge>;
    case 'priority':
      return <Badge variant="warn">PRIORITY</Badge>;
  }
}
